import { useState, useEffect } from 'react';
import { Cloud, Satellite, DollarSign, Leaf, ChevronDown, ChevronUp, AlertCircle, CheckCircle2 } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Step8ExternalData = ({ farmerId, farmerData, currentUser, onDataFetched }) => {
  const [loading, setLoading] = useState({
    weather: false,
    satellite: false,
    pmKisan: false,
    soilHealth: false
  });

  const [expanded, setExpanded] = useState({
    weather: false,
    satellite: false,
    pmKisan: false,
    soilHealth: false
  });

  const [fetchedData, setFetchedData] = useState({
    weather: null,
    satellite: null,
    pmKisan: null,
    soilHealth: null
  });

  const [anyDataFetched, setAnyDataFetched] = useState(false);

  // Hydrate fetched data from existing farmerData on mount / when farmerData changes
  useEffect(() => {
    if (!farmerData) return;

    const hydrated = { weather: null, satellite: null, pmKisan: null, soilHealth: null };
    let hasAny = false;

    const tryParse = (raw) => {
      if (!raw) return null;
      if (typeof raw === 'object') return raw;
      try { return JSON.parse(raw); } catch { return null; }
    };

    if (farmerData.weather_data) {
      hydrated.weather = tryParse(farmerData.weather_data);
      if (hydrated.weather) hasAny = true;
    }
    if (farmerData.satellite_data) {
      hydrated.satellite = tryParse(farmerData.satellite_data);
      if (hydrated.satellite) hasAny = true;
    }
    if (farmerData.pm_kisan_data) {
      hydrated.pmKisan = tryParse(farmerData.pm_kisan_data);
      if (hydrated.pmKisan) hasAny = true;
    }
    if (farmerData.soil_health_data) {
      hydrated.soilHealth = tryParse(farmerData.soil_health_data);
      if (hydrated.soilHealth) hasAny = true;
    }

    setFetchedData(hydrated);
    setAnyDataFetched(hasAny);
  }, [farmerData?.weather_data, farmerData?.satellite_data, farmerData?.pm_kisan_data, farmerData?.soil_health_data]);

  // Helper: get stored timestamp for an integration type
  const getStoredTimestamp = (type) => {
    const tsMap = {
      weather: 'weather_data_fetched_at',
      satellite: 'satellite_data_fetched_at',
      pmKisan: 'pm_kisan_data_fetched_at',
      soilHealth: 'soil_health_data_fetched_at',
    };
    const ts = farmerData?.[tsMap[type]];
    if (!ts) return null;
    try { return new Date(ts).toLocaleString(); } catch { return null; }
  };

  const toggleExpanded = (integration) => {
    setExpanded(prev => ({ ...prev, [integration]: !prev[integration] }));
  };

  const fetchWeatherData = async () => {
    const state = farmerData.residential_state || farmerData.land_state;
    const district = farmerData.residential_district || farmerData.land_district;
    if (!state || !district) {
      alert('Farmer profile must have state and district information to fetch weather data.');
      return;
    }

    setLoading(prev => ({ ...prev, weather: true }));
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${BACKEND_URL}/api/integrations/weather`,
        {
          params: {
            state: state,
            district: district
          },
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      const weatherData = response.data;
      setFetchedData(prev => ({ ...prev, weather: weatherData }));
      setExpanded(prev => ({ ...prev, weather: true }));
      setAnyDataFetched(true);

      // Save to farmer profile
      await saveToFarmerProfile('weather', weatherData);
      
      // Notify parent that data was fetched
      if (onDataFetched) {
        onDataFetched('weather', weatherData);
      }
    } catch (error) {
      console.error('Error fetching weather data:', error);
      alert('Failed to fetch weather data. Please try again.');
    } finally {
      setLoading(prev => ({ ...prev, weather: false }));
    }
  };

  const fetchSatelliteData = async () => {
    setLoading(prev => ({ ...prev, satellite: true }));
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${BACKEND_URL}/api/integrations/satellite/${farmerId}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      const satelliteData = response.data;
      setFetchedData(prev => ({ ...prev, satellite: satelliteData }));
      setExpanded(prev => ({ ...prev, satellite: true }));
      setAnyDataFetched(true);

      // Save to farmer profile
      await saveToFarmerProfile('satellite', satelliteData);
      
      // Notify parent
      if (onDataFetched) {
        onDataFetched('satellite', satelliteData);
      }
    } catch (error) {
      console.error('Error fetching satellite data:', error);
      alert('Failed to fetch satellite data. Please try again.');
    } finally {
      setLoading(prev => ({ ...prev, satellite: false }));
    }
  };

  const fetchPMKisanData = async () => {
    if (!farmerData.aadhaar_number) {
      alert('Aadhaar number is required to fetch PM-KISAN data.');
      return;
    }

    setLoading(prev => ({ ...prev, pmKisan: true }));
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${BACKEND_URL}/api/integrations/pm-kisan/${farmerData.aadhaar_number}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      const pmKisanData = response.data;
      setFetchedData(prev => ({ ...prev, pmKisan: pmKisanData }));
      setExpanded(prev => ({ ...prev, pmKisan: true }));
      setAnyDataFetched(true);

      // Save to farmer profile
      await saveToFarmerProfile('pmKisan', pmKisanData);
      
      // Notify parent
      if (onDataFetched) {
        onDataFetched('pmKisan', pmKisanData);
      }
    } catch (error) {
      console.error('Error fetching PM-KISAN data:', error);
      if (error.response?.status === 403) {
        alert('PM-KISAN data can only be fetched by Credit Officers or System Admins.');
      } else {
        alert('Failed to fetch PM-KISAN data. Please try again.');
      }
    } finally {
      setLoading(prev => ({ ...prev, pmKisan: false }));
    }
  };

  const fetchSoilHealthData = async () => {
    setLoading(prev => ({ ...prev, soilHealth: true }));
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${BACKEND_URL}/api/integrations/soil-health/${farmerId}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      const soilHealthData = response.data;
      setFetchedData(prev => ({ ...prev, soilHealth: soilHealthData }));
      setExpanded(prev => ({ ...prev, soilHealth: true }));
      setAnyDataFetched(true);

      // Save to farmer profile
      await saveToFarmerProfile('soilHealth', soilHealthData);
      
      // Notify parent
      if (onDataFetched) {
        onDataFetched('soilHealth', soilHealthData);
      }
    } catch (error) {
      console.error('Error fetching soil health data:', error);
      alert('Failed to fetch soil health data. Please try again.');
    } finally {
      setLoading(prev => ({ ...prev, soilHealth: false }));
    }
  };

  const saveToFarmerProfile = async (integrationType, data) => {
    try {
      const token = localStorage.getItem('token');
      const updatePayload = {};
      const now = new Date().toISOString();
      
      // Map integration type to field names
      const fieldMapping = {
        weather: {
          data: 'weather_data',
          timestamp: 'weather_data_fetched_at',
          user: 'weather_data_fetched_by'
        },
        satellite: {
          data: 'satellite_data',
          timestamp: 'satellite_data_fetched_at',
          user: 'satellite_data_fetched_by'
        },
        pmKisan: {
          data: 'pm_kisan_data',
          timestamp: 'pm_kisan_data_fetched_at',
          user: 'pm_kisan_data_fetched_by'
        },
        soilHealth: {
          data: 'soil_health_data',
          timestamp: 'soil_health_data_fetched_at',
          user: 'soil_health_data_fetched_by'
        }
      };

      const fields = fieldMapping[integrationType];
      updatePayload[fields.data] = JSON.stringify(data);
      updatePayload[fields.timestamp] = now;
      updatePayload[fields.user] = currentUser.email;

      // Also update individual critical parameters for credit scoring
      if (integrationType === 'weather' && data.seasonal_summary) {
        updatePayload.rainfall_deviation_pct = data.seasonal_summary.rainfall_deviation_pct;
      }
      if (integrationType === 'satellite' && data.vegetation_indices) {
        updatePayload.ndvi_trend_score = data.vegetation_indices.ndvi;
      }
      if (integrationType === 'pmKisan' && data.beneficiary_status !== undefined) {
        updatePayload.pm_kisan_beneficiary_status = data.beneficiary_status === true || data.beneficiary_status === 'active';
      }
      if (integrationType === 'soilHealth' && data.soil_health_score) {
        updatePayload.soil_health_metrics = JSON.stringify(data);
      }

      await axios.put(
        `${BACKEND_URL}/api/farmers/${farmerId}`,
        updatePayload,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
    } catch (error) {
      console.error(`Error saving ${integrationType} data to farmer profile:`, error);
      throw error;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header Section */}
      <div className="space-y-4">
        <div className="flex items-center space-x-2">
          <CheckCircle2 className="h-6 w-6 text-green-600" />
          <h3 className="text-lg font-semibold text-gray-900">Farmer Profile Created Successfully!</h3>
        </div>
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <p className="text-sm text-green-800">
            <strong>Farmer ID:</strong> {farmerId}
          </p>
          <p className="text-sm text-green-800 mt-1">
            You can now fetch external data from government and satellite sources to enhance the credit assessment.
          </p>
        </div>
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start space-x-2">
            <AlertCircle className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
            <div className="space-y-1">
              <h4 className="font-medium text-blue-900">Optional Step</h4>
              <p className="text-sm text-blue-800">
                This step is optional. You can skip if you have connectivity issues or want to fetch data later. External data helps improve credit assessment accuracy.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* External Data Integrations */}
      <div className="space-y-4">
        <h4 className="text-md font-semibold text-gray-900">External Data Integrations</h4>
        <p className="text-sm text-gray-600">
          Fetch real-time data from government and satellite sources. Data is automatically saved to the farmer profile.
        </p>

        {/* Weather Data Card */}
        <div className="border rounded-lg overflow-hidden">
          <div className="flex items-center justify-between p-4 bg-white">
            <div className="flex items-center space-x-3">
              <Cloud className="h-5 w-5 text-blue-600" />
              <div>
                <h5 className="font-medium text-gray-900">Weather Data</h5>
                {fetchedData.weather && (
                  <p className="text-xs text-gray-500">
                    Last fetched: {getStoredTimestamp('weather') || new Date().toLocaleString()}
                  </p>
                )}
              </div>
            </div>
            <div className="flex items-center space-x-2">
              {fetchedData.weather && (
                <button
                  onClick={() => toggleExpanded('weather')}
                  className="p-2 text-gray-500 hover:bg-gray-100 rounded"
                >
                  {expanded.weather ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                </button>
              )}
              <button
                onClick={fetchWeatherData}
                disabled={loading.weather}
                className="px-4 py-2 bg-black text-white rounded-md hover:bg-gray-800 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center space-x-2"
                data-testid="fetch-weather-btn"
              >
                {loading.weather ? (
                  <>
                    <img src="/saakhsetu-loader.webp" alt="" style={{ width: 16, height: 16 }} />
                    <span>Fetching...</span>
                  </>
                ) : (
                  <span>{fetchedData.weather ? 'Refresh' : 'Fetch'}</span>
                )}
              </button>
            </div>
          </div>

          {expanded.weather && fetchedData.weather && (
            <div className="p-4 bg-gray-50 border-t">
              <div className="space-y-3">
                <div className="bg-red-50 border border-red-200 rounded p-3">
                  <p className="text-xs text-gray-600 mb-1">CRITICAL PARAMETER</p>
                  <p className="font-medium text-gray-900">Rainfall Deviation</p>
                  <p className="text-xl font-bold text-red-600">
                    {fetchedData.weather.seasonal_summary?.rainfall_deviation_pct?.toFixed(1)}%
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <p className="text-xs text-gray-600">Temperature</p>
                    <p className="font-medium">{fetchedData.weather.current?.temperature}°C</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-600">Humidity</p>
                    <p className="font-medium">{fetchedData.weather.current?.humidity}%</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-600">Season</p>
                    <p className="font-medium">{fetchedData.weather.seasonal_summary?.season}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-600">Total Rainfall</p>
                    <p className="font-medium">{fetchedData.weather.seasonal_summary?.total_rainfall_mm} mm</p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Satellite Data Card */}
        <div className="border rounded-lg overflow-hidden">
          <div className="flex items-center justify-between p-4 bg-white">
            <div className="flex items-center space-x-3">
              <Satellite className="h-5 w-5 text-purple-600" />
              <div>
                <h5 className="font-medium text-gray-900">Satellite Data</h5>
                {fetchedData.satellite && (
                  <p className="text-xs text-gray-500">
                    Last fetched: {getStoredTimestamp('satellite') || new Date().toLocaleString()}
                  </p>
                )}
              </div>
            </div>
            <div className="flex items-center space-x-2">
              {fetchedData.satellite && (
                <button
                  onClick={() => toggleExpanded('satellite')}
                  className="p-2 text-gray-500 hover:bg-gray-100 rounded"
                >
                  {expanded.satellite ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                </button>
              )}
              <button
                onClick={fetchSatelliteData}
                disabled={loading.satellite}
                className="px-4 py-2 bg-black text-white rounded-md hover:bg-gray-800 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center space-x-2"
                data-testid="fetch-satellite-btn"
              >
                {loading.satellite ? (
                  <>
                    <img src="/saakhsetu-loader.webp" alt="" style={{ width: 16, height: 16 }} />
                    <span>Fetching...</span>
                  </>
                ) : (
                  <span>{fetchedData.satellite ? 'Refresh' : 'Fetch'}</span>
                )}
              </button>
            </div>
          </div>

          {expanded.satellite && fetchedData.satellite && (
            <div className="p-4 bg-gray-50 border-t">
              <div className="space-y-3">
                <div className="bg-green-50 border border-green-200 rounded p-3">
                  <p className="text-xs text-gray-600 mb-1">CRITICAL PARAMETER</p>
                  <p className="font-medium text-gray-900">NDVI Score</p>
                  <p className="text-xl font-bold text-green-600">
                    {fetchedData.satellite.vegetation_indices?.ndvi?.toFixed(2)}
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <p className="text-xs text-gray-600">Interpretation</p>
                    <p className="font-medium">{fetchedData.satellite.vegetation_indices?.ndvi_interpretation}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-600">Crop Health</p>
                    <p className="font-medium">{fetchedData.satellite.crop_health?.status}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-600">Stress Level</p>
                    <p className="font-medium">{fetchedData.satellite.crop_health?.stress_level}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-600">6-Month Trend</p>
                    <p className="font-medium">{fetchedData.satellite.historical_trend?.length || 0} data points</p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* PM-KISAN Card */}
        <div className="border rounded-lg overflow-hidden">
          <div className="flex items-center justify-between p-4 bg-white">
            <div className="flex items-center space-x-3">
              <DollarSign className="h-5 w-5 text-green-600" />
              <div>
                <h5 className="font-medium text-gray-900">PM-KISAN</h5>
                {fetchedData.pmKisan && (
                  <p className="text-xs text-gray-500">
                    Last fetched: {getStoredTimestamp('pmKisan') || new Date().toLocaleString()}
                  </p>
                )}
              </div>
            </div>
            <div className="flex items-center space-x-2">
              {fetchedData.pmKisan && (
                <button
                  onClick={() => toggleExpanded('pmKisan')}
                  className="p-2 text-gray-500 hover:bg-gray-100 rounded"
                >
                  {expanded.pmKisan ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                </button>
              )}
              <button
                onClick={fetchPMKisanData}
                disabled={loading.pmKisan}
                className="px-4 py-2 bg-black text-white rounded-md hover:bg-gray-800 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center space-x-2"
                data-testid="fetch-pmkisan-btn"
              >
                {loading.pmKisan ? (
                  <>
                    <img src="/saakhsetu-loader.webp" alt="" style={{ width: 16, height: 16 }} />
                    <span>Fetching...</span>
                  </>
                ) : (
                  <span>{fetchedData.pmKisan ? 'Refresh' : 'Fetch'}</span>
                )}
              </button>
            </div>
          </div>

          {expanded.pmKisan && fetchedData.pmKisan && (
            <div className="p-4 bg-gray-50 border-t">
              <div className="space-y-3">
                <div className={`rounded p-3 ${
                  fetchedData.pmKisan.beneficiary_status === true || fetchedData.pmKisan.beneficiary_status === 'active'
                    ? 'bg-green-50 border border-green-200' 
                    : 'bg-gray-50 border border-gray-200'
                }`}>
                  <p className="text-xs text-gray-600 mb-1">Beneficiary Status</p>
                  <p className="text-xl font-bold capitalize">
                    {fetchedData.pmKisan.beneficiary_status === true ? 'Active' : fetchedData.pmKisan.beneficiary_status === false ? 'Inactive' : String(fetchedData.pmKisan.beneficiary_status)}
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <p className="text-xs text-gray-600">Total Received</p>
                    <p className="font-medium text-green-600">₹{fetchedData.pmKisan.total_received || fetchedData.pmKisan.total_amount_received || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-600">Registration Date</p>
                    <p className="font-medium">{fetchedData.pmKisan.registration_date || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-600">Last Payment</p>
                    <p className="font-medium">{fetchedData.pmKisan.last_payment_date || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-600">Payment History</p>
                    <p className="font-medium">{fetchedData.pmKisan.payment_history?.length || 0} installments</p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Soil Health Card */}
        <div className="border rounded-lg overflow-hidden">
          <div className="flex items-center justify-between p-4 bg-white">
            <div className="flex items-center space-x-3">
              <Leaf className="h-5 w-5 text-yellow-600" />
              <div>
                <h5 className="font-medium text-gray-900">Soil Health</h5>
                {fetchedData.soilHealth && (
                  <p className="text-xs text-gray-500">
                    Last fetched: {getStoredTimestamp('soilHealth') || new Date().toLocaleString()}
                  </p>
                )}
              </div>
            </div>
            <div className="flex items-center space-x-2">
              {fetchedData.soilHealth && (
                <button
                  onClick={() => toggleExpanded('soilHealth')}
                  className="p-2 text-gray-500 hover:bg-gray-100 rounded"
                >
                  {expanded.soilHealth ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                </button>
              )}
              <button
                onClick={fetchSoilHealthData}
                disabled={loading.soilHealth}
                className="px-4 py-2 bg-black text-white rounded-md hover:bg-gray-800 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center space-x-2"
                data-testid="fetch-soilhealth-btn"
              >
                {loading.soilHealth ? (
                  <>
                    <img src="/saakhsetu-loader.webp" alt="" style={{ width: 16, height: 16 }} />
                    <span>Fetching...</span>
                  </>
                ) : (
                  <span>{fetchedData.soilHealth ? 'Refresh' : 'Fetch'}</span>
                )}
              </button>
            </div>
          </div>

          {expanded.soilHealth && fetchedData.soilHealth && (
            <div className="p-4 bg-gray-50 border-t">
              <div className="space-y-3">
                <div className="bg-yellow-50 border border-yellow-200 rounded p-3">
                  <p className="text-xs text-gray-600 mb-1">Soil Health Score</p>
                  <p className="text-xl font-bold text-yellow-700">
                    {fetchedData.soilHealth.soil_health_score}/100
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <p className="text-xs text-gray-600">Nitrogen (N)</p>
                    <span className="inline-block px-2 py-1 bg-black text-white text-xs rounded">
                      {fetchedData.soilHealth.soil_analysis?.nitrogen || 'N/A'}
                    </span>
                  </div>
                  <div>
                    <p className="text-xs text-gray-600">Phosphorus (P)</p>
                    <span className="inline-block px-2 py-1 bg-black text-white text-xs rounded">
                      {fetchedData.soilHealth.soil_analysis?.phosphorus || 'N/A'}
                    </span>
                  </div>
                  <div>
                    <p className="text-xs text-gray-600">Potassium (K)</p>
                    <span className="inline-block px-2 py-1 bg-black text-white text-xs rounded">
                      {fetchedData.soilHealth.soil_analysis?.potassium || 'N/A'}
                    </span>
                  </div>
                  <div>
                    <p className="text-xs text-gray-600">pH Level</p>
                    <p className="font-medium">{fetchedData.soilHealth.soil_analysis?.ph_level || 'N/A'}</p>
                  </div>
                  <div className="col-span-2">
                    <p className="text-xs text-gray-600">Recommendations</p>
                    <p className="font-medium">{fetchedData.soilHealth.recommendations?.length || 0}</p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Information Box */}
      <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
        <h4 className="font-medium text-blue-900 mb-2">About External Data</h4>
        <p className="text-sm text-blue-800">
          {anyDataFetched 
            ? 'External data has been successfully fetched and saved to the farmer profile. Credit score will be automatically recalculated with this new data.' 
            : 'Fetching external data is optional but recommended. It helps create a more accurate credit assessment by incorporating real-time government and satellite information.'}
        </p>
      </div>
    </div>
  );
};

export default Step8ExternalData;
